<template>
  <div class="pago-overlay" @click.self="cerrar">
    <div class="pago-caja">
      <div class="pago-cabecera">
        <div>
          <h3>{{ titulo }}</h3>
          <p v-if="importeBonito" class="pago-importe">{{ importeBonito }}</p>
        </div>
        <button class="pago-cerrar" :disabled="pagando" aria-label="Cerrar" @click="cerrar">&times;</button>
      </div>

      <div class="pago-cuerpo">
        <div v-if="cargando" class="pago-estado">
          <span class="pago-girando"></span>
          <span>Preparando el pago seguro…</span>
        </div>

        <!-- Stripe monta aquí su formulario, dentro de un iframe suyo: los datos de
             la tarjeta no pasan por esta página ni por nuestro servidor. -->
        <div v-show="!cargando" ref="contenedor" class="pago-elemento"></div>

        <p v-if="error" class="pago-error">{{ error }}</p>

        <p class="pago-seguro">
          Pago cifrado y procesado por Stripe. No guardamos los datos de tu tarjeta.
        </p>
      </div>

      <div class="pago-botonera">
        <button class="pago-btn" :disabled="pagando" @click="cerrar">Cancelar</button>
        <button class="pago-btn pago-btn-primario" :disabled="cargando || pagando" @click="pagar">
          <span v-if="pagando" class="pago-girando pago-girando-pequeno"></span>
          {{ pagando ? "Procesando…" : "Pagar" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { cargarStripe } from "@/helpers/pasarelaPago";

export default {
  name: "PagoConTarjeta",

  props: {
    clientSecret: { type: String, required: true },
    publishableKey: { type: String, required: true },
    titulo: { type: String, default: "Pagar con tarjeta" },
    importe: { type: [Number, String], default: null },
    moneda: { type: String, default: "USD" },
  },

  data() {
    return {
      cargando: true,
      pagando: false,
      error: "",
      stripe: null,
      elements: null,
    };
  },

  computed: {
    importeBonito() {
      const n = Number(this.importe);
      return Number.isFinite(n) && n > 0 ? this.moneda + " " + n.toFixed(2) : "";
    },
  },

  async mounted() {
    try {
      const Stripe = await cargarStripe();
      this.stripe = Stripe(this.publishableKey);

      this.elements = this.stripe.elements({
        clientSecret: this.clientSecret,
        appearance: {
          theme: "stripe",
          variables: { colorPrimary: "#10b981", borderRadius: "8px", fontFamily: "inherit" },
        },
      });

      this.elements.create("payment", { layout: "tabs" }).mount(this.$refs.contenedor);
      this.cargando = false;
    } catch (e) {
      this.cargando = false;
      this.error = (e && e.message) || "No se pudo preparar el formulario de pago.";
      this.$emit("error", this.error);
    }
  },

  beforeDestroy() {
    // Sin desmontarlo, el iframe de Stripe sigue vivo y el siguiente pago arranca
    // sobre un formulario viejo.
    try {
      const el = this.elements && this.elements.getElement("payment");
      if (el) el.unmount();
    } catch (e) {
      /* ya estaba desmontado */
    }
  },

  methods: {
    async pagar() {
      if (!this.stripe || !this.elements || this.pagando) return;

      this.pagando = true;
      this.error = "";

      // 'if_required' evita salir de la página cuando el banco no pide 3D Secure,
      // que es la mayoría de las veces.
      const resultado = await this.stripe.confirmPayment({
        elements: this.elements,
        redirect: "if_required",
        confirmParams: { return_url: window.location.href },
      });

      const fallo = resultado.error;
      const intento = resultado.paymentIntent;

      if (fallo) {
        this.pagando = false;
        this.error = (fallo.type === "card_error" || fallo.type === "validation_error")
          ? fallo.message
          : "No se pudo completar el pago. Inténtalo de nuevo.";
        this.$emit("error", this.error);
        return;
      }

      if (intento && intento.status === "succeeded") {
        this.$emit("pagado", intento.id);
        return;
      }

      this.pagando = false;
      this.error = "El pago quedó en estado «" + ((intento && intento.status) || "desconocido") + "».";
      this.$emit("error", this.error);
    },

    cerrar() {
      if (this.pagando) return;
      this.$emit("cancelado");
    },
  },
};
</script>

<style scoped>
.pago-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 16px;
}

.pago-caja {
  background: #fff;
  color: #0f172a;
  border-radius: 14px;
  width: 100%;
  max-width: 460px;
  max-height: 92vh;
  overflow-y: auto;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
}

.pago-cabecera {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 18px 20px 10px;
  border-bottom: 1px solid #e2e8f0;
}

.pago-cabecera h3 { margin: 0; font-size: 1.05rem; font-weight: 700; }

.pago-importe { margin: 4px 0 0; font-size: 1.3rem; font-weight: 700; color: #10b981; }

.pago-cerrar {
  background: none;
  border: none;
  font-size: 1.6rem;
  line-height: 1;
  cursor: pointer;
  color: #64748b;
}

.pago-cerrar:disabled { opacity: 0.4; cursor: not-allowed; }

.pago-cuerpo { padding: 18px 20px; }

.pago-elemento { min-height: 180px; }

.pago-estado {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 40px 0;
  color: #64748b;
  font-size: 0.9rem;
}

.pago-girando {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(16, 185, 129, 0.25);
  border-top-color: #10b981;
  border-radius: 50%;
  animation: pago-giro 0.8s linear infinite;
  display: inline-block;
}

.pago-girando-pequeno { width: 13px; height: 13px; margin-right: 6px; vertical-align: -2px; }

@keyframes pago-giro { to { transform: rotate(360deg); } }

.pago-error {
  margin: 12px 0 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(239, 68, 68, 0.12);
  color: #b91c1c;
  font-size: 0.85rem;
}

.pago-seguro { margin: 14px 0 0; font-size: 0.75rem; color: #64748b; text-align: center; }

.pago-botonera {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding: 12px 20px 18px;
}

.pago-btn {
  padding: 9px 18px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: transparent;
  color: inherit;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
}

.pago-btn:disabled { opacity: 0.55; cursor: not-allowed; }

.pago-btn-primario { background: #10b981; border-color: #10b981; color: #fff; }
</style>
